import React, { useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import { readStoredUser } from "../../../utils/sessionUser";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { getDocumentTranslator } from "./documentPopupMessages";
import PurchasingDocumentDialog from "./PurchasingDocumentDialog";

export default function SaveRequisitionTemplateDialog({ lines, businessUnitId, onClose, onSaved }) {
  const { languageId } = usePmsTheme();
  const t = getDocumentTranslator(languageId);
  const [ownerId] = useState(() => readStoredUser()?.id);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const request = useRef(null);
  useEffect(() => () => request.current?.abort(), []);

  const save = async () => {
    if (request.current) return;
    const trimmedName = name.trim();
    if (!trimmedName) { setError("Enter a name for this template."); return; }
    const user = readStoredUser();
    if (Number(user?.id) !== 1 && user?.permissions?.["purchasing.requisitions.create"] !== true) {
      setError("Access denied: insufficient permissions");
      return;
    }
    if (!businessUnitId || String(user?.id) !== String(ownerId) || String(user?.active_business_unit_id) !== String(businessUnitId)) {
      setError("The session or business unit changed. Reopen this requisition before saving a template.");
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    setSaving(true);
    setError("");
    try {
      await axios.post("/api/purchasing/templates", {
        business_unit_id: businessUnitId,
        name: trimmedName,
        // Copy reusable values, never persisted requisition/line identities.
        lines: (lines || []).filter((line) => line?.item_id).map((line) => ({
          item_id: line.item_id, supplier_id: line.supplier_id, quantity: line.quantity,
          display_unit_level: line.display_unit_level || "unit", subunit2_id: line.subunit2_id, subunit2_size: line.subunit2_size,
          unit_id: line.unit_id, pack_size: line.pack_size, subunit_id: line.subunit_id,
          unit_price: line.unit_price, last_price_info: line.last_price_info, notes: line.notes,
        })),
      }, { signal: controller.signal, pilcoreExpectedUserId: ownerId,
        headers: { "X-Pilcore-Business-Unit-Id": businessUnitId } });
      if (!controller.signal.aborted) onSaved(trimmedName);
    } catch (requestError) {
      if (!controller.signal.aborted) setError(requestError?.response?.data?.code === "REQUISITION_UNIT_CONFIGURATION_STALE"
        ? "The item's units or packaging have changed. Reload the requisition and review its quantities before saving."
        : requestError?.response?.data?.error || "Error saving template.");
    } finally {
      if (!controller.signal.aborted) { request.current = null; setSaving(false); }
    }
  };

  return <PurchasingDocumentDialog isOpen title={t("Save as a template")} onClose={onClose}
    onConfirm={save} isBusy={saving} confirmLabel={t(saving ? "Saving..." : "Save Template")}
    message={<>
      <p>{t("Name the current requisition setup and save it as a reusable Pilcore template.")}</p>
      <label className="pu-template-label" htmlFor="requisition-template-name">{t("Template Name")}</label>
      <input id="requisition-template-name" className="pu-template-input" value={name} maxLength={100}
        disabled={saving} onChange={(event) => setName(event.target.value)}
        placeholder={t("Example: Weekly breakfast replenishment")}
        onKeyDown={(event) => { if (event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); save(); } }} />
      <p>{t("We will save the current requisition lines exactly as they are now, so you can reuse them later in one click.")}</p>
      {error && <p role="alert">{t(error)}</p>}
    </>} />;
}
